import React from 'react'
import { Link } from '@tanstack/react-router'
import { CHIP_TONES } from './chip-tones'
import NewsletterCta, { DEFAULT_NEWSLETTER_COPY } from './newsletter-cta'
import type { PostSummary } from './posts'
import type { Chip } from './post-parser'

/**
 * The /blog landing page: the newest post given a split hero, the rest as a
 * ruled list beneath it, closing with the same newsletter sign-up every post
 * ends on.
 *
 * Everything it shows comes from the posts' own frontmatter via `blogIndex`
 * (see posts.ts), so publishing a post adds it here with no edit — including
 * taking over the featured slot when it's the newest.
 */

// The chip pill, shared with the post hero (blog-post.tsx) so a post's
// categories look the same in the feed as they do on the page.
const chipClass =
   'inline-flex items-center px-[13px] py-[5px] rounded-full text-[11.5px] font-bold tracking-[0.09em] uppercase'

const ToneChip = ({ chip }: { chip: Chip }) => (
   <span
      className={chipClass}
      style={{ background: CHIP_TONES[chip.tone].wash, color: CHIP_TONES[chip.tone].ink }}
   >
      {chip.label}
   </span>
)

// author · date · read time, skipping whatever a post hasn't filled in
// (`readTime` is optional frontmatter).
const Byline = ({ parts, className }: { parts: string[]; className: string }) => (
   <div className={className}>
      {parts.map((part, index) => (
         <React.Fragment key={index}>
            {index > 0 && <span aria-hidden="true">·</span>}
            <span>{part}</span>
         </React.Fragment>
      ))}
   </div>
)

const FeaturedPost = ({ post }: { post: PostSummary }) => {
   const { slug, meta } = post
   return (
      <article className="grid md:grid-cols-[1.1fr_0.9fr] md:items-center gap-[24px] md:gap-[40px] pt-[32px] md:pt-[40px] pb-[32px] md:pb-[40px] border-b border-hairline">
         <div className="flex flex-col items-start gap-[18px]">
            <div className="flex flex-wrap gap-[8px]">
               <span className={`${chipClass} bg-lightBlueTransparent text-translit`}>
                  Latest post
               </span>
               {meta.chips.map((chip, index) => (
                  <ToneChip key={index} chip={chip} />
               ))}
            </div>
            <h2 className="m-0 text-[32px] md:text-[44px] font-extrabold leading-[1.1] tracking-[-0.018em] text-balance">
               <Link
                  to="/blog/$blogSlug"
                  params={{ blogSlug: slug }}
                  className="text-bodyBlack no-underline hover:text-translit transition-colors duration-150"
               >
                  {meta.title}
               </Link>
            </h2>
            <p className="m-0 text-[17px] md:text-[18px] leading-[1.55] text-bodyGray text-pretty">
               {meta.subtitle}
            </p>
            <Byline
               parts={[meta.author, meta.date, meta.readTime].filter(Boolean)}
               className="flex items-center gap-[8px] text-[13.5px] text-textMuted"
            />
            <Link
               to="/blog/$blogSlug"
               params={{ blogSlug: slug }}
               className="inline-flex items-center justify-center gap-[8px] px-[30px] py-[11px] rounded-[8px] bg-translit text-[16px] font-bold text-white no-underline transition-colors duration-150 hover:bg-[#2559d6] hover:text-white"
            >
               Read the update
            </Link>
         </div>
         {meta.heroImage && (
            // Widths chosen so the 4:5 post illustrations come in just under the
            // height cap — otherwise `object-contain` letterboxes them and the
            // slack reads as a gap above the rule. The cap still guards the row
            // against a future post whose art is a different shape.
            <img
               className="block w-full max-w-[300px] md:max-w-[340px] max-h-[440px] object-contain mx-auto md:mx-0 md:ml-auto"
               src={meta.heroImage}
               alt={meta.heroAlt}
            />
         )}
      </article>
   )
}

const PostRow = ({ post }: { post: PostSummary }) => {
   const { slug, meta } = post
   return (
      <Link
         to="/blog/$blogSlug"
         params={{ blogSlug: slug }}
         // Below md the date sits above a thumbnail-beside-text pair; at md the
         // `md:contents` wrapper dissolves so all three become grid columns.
         className="flex flex-col gap-[12px] md:grid md:grid-cols-[132px_140px_minmax(0,1fr)] md:gap-[24px] md:items-start border-t border-hairline rounded-[8px] -mx-[12px] px-[12px] py-[24px] text-inherit no-underline transition-colors duration-150 hover:bg-lightBlueTransparent"
      >
         <div className="md:pt-[3px] text-[13.5px] leading-[1.5] text-textMuted">{meta.date}</div>
         <div className="flex gap-[16px] md:contents">
            {/* 4:5 is the shape the post illustrations are drawn at
                (1122×1402), so `object-cover` fills the tile with neither a
                crop nor a letterbox. */}
            <div className="shrink-0 w-[112px] md:w-full aspect-[4/5] grid place-items-center overflow-hidden rounded-[8px] border border-hairline bg-chipBg">
               {meta.heroImage ? (
                  <img
                     className="w-full h-full object-cover"
                     src={meta.heroImage}
                     alt={meta.heroAlt}
                     loading="lazy"
                  />
               ) : (
                  <span className="material-icons text-[26px] text-textMuted">image</span>
               )}
            </div>
            <div className="min-w-0">
               {meta.chips.length > 0 && <ToneChip chip={meta.chips[0]} />}
               <h3 className="mt-[12px] mb-0 text-[21px] md:text-[24px] font-extrabold leading-[1.25] text-bodyBlack text-pretty">
                  {meta.title}
               </h3>
               <p className="mt-[8px] mb-0 text-[16px] leading-[1.6] text-bodyGray text-pretty">
                  {meta.subtitle}
               </p>
               {meta.readTime && (
                  <div className="mt-[10px] text-[13px] text-textMuted">{meta.readTime}</div>
               )}
            </div>
         </div>
      </Link>
   )
}

const BlogIndex = ({ posts }: { posts: PostSummary[] }) => {
   const [featured, ...rest] = posts

   return (
      <main className="bg-white font-latin text-blogGray px-[20px]">
         {/* The site's measure: the same 900px the header's logo and search box
             sit on, so the hero lines up with the chrome above it. */}
         <div className="max-w-[900px] mx-auto">
            {/* The page's own title is the section, not the featured post's
                headline — so it's read out but not drawn. */}
            <h1 className="sr-only">The thai2english blog</h1>

            {featured && <FeaturedPost post={featured} />}

            {rest.length > 0 && (
               <section className="pt-[36px] pb-[8px]">
                  <h2 className="m-0 mb-[4px] text-[12px] font-extrabold tracking-[0.09em] uppercase text-textMuted">
                     More from the blog
                  </h2>
                  {rest.map((post) => (
                     <PostRow key={post.slug} post={post} />
                  ))}
               </section>
            )}

            <NewsletterCta copy={DEFAULT_NEWSLETTER_COPY} />
         </div>
      </main>
   )
}

export default BlogIndex
